import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {PoolDepositFormData} from '@/screens/Merchants/Transactions/List/types';
import type {AdminPaginatedEnvelope, AfriexQueryParams} from '@/types';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {parseBlobErrorBody} from '@/utils/downloadFile';

export const merchantTransactionService = {
  updateBusinessTransaction: async function (
    transactionID: string,
    data: AfriexQueryParams,
  ): Promise<unknown> {
    const response = await axios.patch<unknown>(
      `/v2/admin/business/transactions/${transactionID}`,
      data,
    );
    return response.data;
  },
  getBusinessTransactionList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexBusinessTransaction[]>> => {
    const urlParams = toQueryString({page: 0, limit: 10, ...filters});
    const url = `/v2/admin/business/transactions?${urlParams}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexBusinessTransaction[]>>(url);
    return response.data;
  },
  // The whole filtered list as a CSV, streamed by the server. Only the
  // filters are sent: the server ignores paging for a download.
  downloadBusinessTransactionList: async (
    filters: AfriexQueryParams,
  ): Promise<Blob> => {
    const urlParams = toQueryString({...filters, action: 'DOWNLOAD'});
    const url = `/v2/admin/business/transactions?${urlParams}`;
    try {
      const response = await axios.get<Blob>(url, {responseType: 'blob'});
      return response.data;
    } catch (error) {
      throw await parseBlobErrorBody(error);
    }
  },
  // The business user's account statement for the range as a PDF, built and
  // streamed by the server (Merchants > Users > Statement > Download).
  downloadBusinessUserStatement: async (
    businessUserId: string,
    filters: AfriexQueryParams,
  ): Promise<Blob> => {
    const url = `/v2/admin/business/users/${businessUserId}/statement?${toQueryString({...filters})}`;
    try {
      const response = await axios.get<Blob>(url, {responseType: 'blob'});
      return response.data;
    } catch (error) {
      throw await parseBlobErrorBody(error);
    }
  },
  getBusinessTransaction: async ({
    id,
  }: {
    id: string;
  }): Promise<AfriexBusinessTransaction> => {
    const url = `/v2/admin/business/transactions/${id}`;
    const response = await axios.get<AfriexBusinessTransaction>(url);
    return response.data;
  },

  confirmPoolDeposit: async (
    // `acknowledgeDuplicate` is not form data; it is the operator's explicit
    // override of the server's duplicate guard, sent only on a retry.
    data: PoolDepositFormData & {acknowledgeDuplicate?: boolean},
  ): Promise<{status: string}> => {
    const {paymentSentAt, ...rest} = data;
    const response = await axios.post<{status: string}>(
      '/v2/admin/business/pool-accounts/confirm-deposit',
      {
        ...rest,
        // The picker works in Date; the wire carries ISO 8601.
        paymentSentAt: paymentSentAt?.toISOString(),
      },
    );
    return response.data;
  },

  /**
   * Approve an entity-submitted pool deposit: confirms the inflow exists,
   * credits the wallet, and fires the entity webhook. `amount` is only sent
   * when correcting the figure the entity declared.
   */
  approvePoolDeposit: async (
    transactionId: string,
    payload: {amount?: number; acknowledgeDuplicate?: boolean} = {},
    // The route returns only a minimal `{status}` ack (the settled transaction
    // carries non-client meta and is capped by the response whitelist); the
    // review UI toasts + reloads and never reads a transaction off the body.
  ): Promise<{status: string}> => {
    const response = await axios.post<{status: string}>(
      `/v2/admin/business/pool-deposit-requests/${transactionId}/approve`,
      payload,
    );
    return response.data;
  },

  rejectPoolDeposit: async (
    transactionId: string,
    payload: {reason: string; resubmissionRequired: boolean},
    // Minimal `{status}` ack, same as approve above.
  ): Promise<{status: string}> => {
    const response = await axios.post<{status: string}>(
      `/v2/admin/business/pool-deposit-requests/${transactionId}/reject`,
      payload,
    );
    return response.data;
  },

  resolvePoolReference: async (
    reference: string,
  ): Promise<{
    found: boolean;
    businessId?: string;
    userId?: string;
    businessName?: string;
    customerName?: string;
  }> => {
    const {data} = await axios.get<{
      found: boolean;
      businessId?: string;
      userId?: string;
      businessName?: string;
      customerName?: string;
    }>('/v2/admin/business/pool-accounts/resolve-reference', {
      params: {reference},
    });
    return data;
  },
};
